import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { Reveal, SectionHead } from "@/components/site/Reveal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Pricing() {
  useSeo({ title: "Pricing", description: "Transparent starting points for Buddy Solution's digital services. Every engagement is tailored to your goals." });
  const { data: packages = [] } = useQuery({ queryKey: ["packages"], queryFn: async () => (await api.get("/packages")).data });

  return (
    <div className="bs-container py-16 lg:py-20">
      <Reveal><SectionHead center eyebrow="Pricing" title="Clear, honest starting points"
        subtitle="No hidden fees, no fabricated discounts. These packages are a starting point; final quotes are tailored to scope." /></Reveal>

      <div className="mt-14 grid gap-6 lg:grid-cols-3">
        {packages.map((p, i) => (
          <Reveal key={p.id || i} delay={i * 0.06}>
            <div className={`relative flex h-full flex-col bs-card p-7 ${p.popular ? "border-primary shadow-glow" : ""}`}>
              {p.popular && <Badge className="absolute -top-3 left-7">Most popular</Badge>}
              <h3 className="font-display text-xl font-bold">{p.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
              <div className="mt-5">
                {p.price > 0
                  ? <p className="font-display text-4xl font-black">${p.price.toLocaleString()}<span className="text-sm font-medium text-muted-foreground"> {p.period}</span></p>
                  : <p className="font-display text-4xl font-black">Custom</p>}
              </div>
              <ul className="mt-6 flex-1 space-y-3">
                {p.features.map((f) => <li key={f} className="flex items-start gap-2.5 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {f}</li>)}
              </ul>
              <Button asChild className="mt-7 w-full rounded-full" variant={p.popular ? "default" : "outline"} data-testid={`pricing-page-cta-${i}`}>
                <Link to="/contact">{p.cta}</Link>
              </Button>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-16 rounded-3xl border border-border bg-card/40 p-8 text-center">
        <h3 className="font-display text-2xl font-bold">Need something custom?</h3>
        <p className="mx-auto mt-2 max-w-xl text-muted-foreground">Most of our work is bespoke. Book a free consultation and we'll build a plan around your goals and budget.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button asChild className="rounded-full"><Link to="/consultation">Get Free Consultation</Link></Button>
          <Button asChild variant="outline" className="rounded-full"><Link to="/contact">Request a Quote</Link></Button>
        </div>
      </div>
    </div>
  );
}
